/**
 * 大屏固定指令语音：Web Speech API + 白名单严格全匹配。
 * 原则：宁可没反应，绝对不乱跳；只认完整口令，不含别名/模糊说法。
 */

const IDLE_OFF_MS = 10 * 60 * 1000;
const DEBOUNCE_MS = 1500;
/** 浏览器给出置信度且 >0 时才卡阈值；0/缺失视为未提供，仍靠白名单 */
const MIN_CONFIDENCE = 0.7;

/** 短语按长度降序匹配，长词优先，避免短词误伤 */
const COMMANDS = [
  {
    action: "open",
    path: "/overview",
    label: "打开总览",
    phrases: ["打开总览"]
  },
  {
    action: "open",
    path: "/thought",
    label: "打开思想引领",
    phrases: ["打开思想引领"]
  },
  {
    action: "open",
    path: "/org",
    label: "打开组织建设",
    phrases: ["打开组织建设"]
  },
  {
    action: "open",
    path: "/duty",
    label: "打开履职建言",
    phrases: ["打开履职建言"]
  },
  {
    action: "open",
    path: "/activity",
    label: "打开活动公益",
    phrases: ["打开活动公益"]
  },
  {
    action: "open",
    path: "/mechanism",
    label: "打开机制创新",
    phrases: ["打开机制创新"]
  },
  {
    action: "open",
    path: "/branch",
    label: "打开分会数据",
    phrases: ["打开分会数据"]
  },
  {
    action: "home",
    label: "返回首页",
    phrases: ["返回首页"]
  },
  {
    action: "enterInspect",
    label: "进入迎检",
    phrases: ["进入迎检"]
  },
  {
    action: "exitInspect",
    label: "退出迎检",
    phrases: ["退出迎检"]
  },
  {
    action: "next",
    label: "下一页",
    phrases: ["下一页"]
  },
  {
    action: "prev",
    label: "上一页",
    phrases: ["上一页"]
  }
];

const PHRASE_INDEX = (() => {
  const rows = [];
  COMMANDS.forEach(cmd => {
    (cmd.phrases || []).forEach(p => {
      rows.push({ phrase: normalize(p), cmd });
    });
  });
  rows.sort((a, b) => b.phrase.length - a.phrase.length);
  return rows;
})();

export function isSpeechRecognitionSupported() {
  return !!(
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition)
  );
}

export function normalize(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[\s\u3000]/g, "")
    .replace(/[，。！？、,.!?;；：:·…—～~（）()【】《》<>「」『』[\]"'“”‘’]/g, "");
}

/**
 * @returns {{ action: string, label: string, path?: string } | null}
 */
export function matchVoiceCommand(transcript) {
  const text = normalize(transcript);
  if (!text || text.length < 2) return null;
  for (let i = 0; i < PHRASE_INDEX.length; i++) {
    const row = PHRASE_INDEX[i];
    if (row.phrase && text === row.phrase) {
      return {
        action: row.cmd.action,
        label: row.cmd.label,
        path: row.cmd.path
      };
    }
  }
  return null;
}

function confidenceOk(alt) {
  if (!alt) return false;
  const c = alt.confidence;
  if (c == null || c === 0 || Number.isNaN(c)) return true;
  return c >= MIN_CONFIDENCE;
}

/**
 * @param {{ onCommand: Function, onStateChange?: Function, onError?: Function }} opts
 */
export function createVoiceCommander(opts) {
  const onCommand = opts && opts.onCommand;
  const onStateChange = (opts && opts.onStateChange) || function() {};
  const onError = (opts && opts.onError) || function() {};

  let recognition = null;
  let enabled = false;
  let intentionalStop = false;
  let lastHitAt = 0;
  let idleTimer = null;
  let restartTimer = null;

  function emitState() {
    onStateChange({ enabled, supported: isSpeechRecognitionSupported() });
  }

  function clearIdle() {
    clearTimeout(idleTimer);
    idleTimer = null;
  }

  function bumpIdle() {
    clearIdle();
    if (!enabled) return;
    idleTimer = setTimeout(() => {
      stop("idle");
    }, IDLE_OFF_MS);
  }

  function clearRestart() {
    clearTimeout(restartTimer);
    restartTimer = null;
  }

  /** 先申请麦克风；返回 { ok, errorName?, errorMessage? } */
  function ensureMicPermission() {
    if (
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== "function"
    ) {
      return Promise.resolve({
        ok: false,
        errorName: "NotSupportedError",
        errorMessage: "当前页面无法调用麦克风 API"
      });
    }
    return navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(stream => {
        try {
          stream.getTracks().forEach(t => t.stop());
        } catch (e) {
          /* ignore */
        }
        return { ok: true };
      })
      .catch(err => ({
        ok: false,
        errorName: (err && err.name) || "Error",
        errorMessage: (err && err.message) || ""
      }));
  }

  function ensureRecognition() {
    if (recognition) return recognition;
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Ctor) return null;
    const rec = new Ctor();
    rec.lang = "zh-CN";
    rec.continuous = true;
    rec.interimResults = false;
    rec.maxAlternatives = 1;

    rec.onresult = event => {
      if (!enabled) return;
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (!result || !result.isFinal) continue;
        const alt = result[0];
        if (!confidenceOk(alt)) continue;
        const transcript = (alt && alt.transcript) || "";
        const hit = matchVoiceCommand(transcript);
        if (!hit) continue;
        const now = Date.now();
        if (now - lastHitAt < DEBOUNCE_MS) continue;
        lastHitAt = now;
        bumpIdle();
        if (typeof onCommand === "function") {
          onCommand(hit, transcript);
        }
      }
    };

    rec.onerror = event => {
      const err = event && event.error;
      // no-speech / aborted：开启态下静默，由 onend 重启
      if (err === "not-allowed" || err === "service-not-allowed") {
        stop("denied");
        onError(err === "service-not-allowed" ? "service-not-allowed" : "not-allowed");
      } else if (err === "network") {
        onError("network");
      }
    };

    rec.onend = () => {
      clearRestart();
      if (!enabled || intentionalStop) return;
      // Chrome 会周期性结束会话，开启态下自动续听
      restartTimer = setTimeout(() => {
        if (!enabled || intentionalStop) return;
        try {
          recognition.start();
        } catch (e) {
          /* already started */
        }
      }, 280);
    };

    recognition = rec;
    return recognition;
  }

  function startRecognition() {
    const rec = ensureRecognition();
    if (!rec) {
      emitState();
      return { ok: false, reason: "unsupported" };
    }
    intentionalStop = false;
    enabled = true;
    bumpIdle();
    emitState();
    try {
      rec.start();
      return { ok: true };
    } catch (e) {
      // InvalidStateError: already started
      return { ok: true };
    }
  }

  function start() {
    if (!isSpeechRecognitionSupported()) {
      emitState();
      return Promise.resolve({ ok: false, reason: "unsupported" });
    }
    // 非安全上下文（非 HTTPS / 非 localhost）时 Chrome 会直接拒麦
    if (
      typeof window !== "undefined" &&
      window.isSecureContext === false
    ) {
      return Promise.resolve({ ok: false, reason: "insecure" });
    }
    return ensureMicPermission().then(mic => {
      if (!mic || !mic.ok) {
        emitState();
        return {
          ok: false,
          reason: "mic-denied",
          errorName: (mic && mic.errorName) || "Error",
          errorMessage: (mic && mic.errorMessage) || "",
          origin: typeof location !== "undefined" ? location.origin : "",
          secure: typeof window !== "undefined" ? !!window.isSecureContext : false
        };
      }
      return startRecognition();
    });
  }

  function stop(reason) {
    intentionalStop = true;
    enabled = false;
    clearIdle();
    clearRestart();
    if (recognition) {
      try {
        recognition.abort();
      } catch (e) {
        try {
          recognition.stop();
        } catch (e2) {
          /* ignore */
        }
      }
    }
    emitState();
    return { ok: true, reason: reason || "manual" };
  }

  function toggle() {
    if (enabled) return Promise.resolve(stop("manual"));
    return start();
  }

  function destroy() {
    stop("destroy");
    if (recognition) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition = null;
    }
  }

  return {
    start,
    stop,
    toggle,
    destroy,
    bumpIdle,
    isEnabled: () => enabled,
    isSupported: isSpeechRecognitionSupported
  };
}
